import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    ChildrenProps,
} from "../customTypes/valueTypes";
import { CommonResponseModel, userDetailValueModel } from "../Models/tableDetailsModel";
import agent from "../API/apiCalls";
import { activeSectionInfo } from "../Models/applicationModels";
import authService from "../utils/authService";
import { JWT_KEY_FROM_URL } from "../constants/jwtConstant";
import { AuthStatusEnum } from "../constants/authConstants";
import { DEFAULT_ACTIVE_SECTION, EMPTY_USER_DETAIL } from "../constants/defaultValues";

const UserContext = React.createContext<UserPayload | null>(null);

type AuthStatus = AuthStatusEnum.IDLE | AuthStatusEnum.LOADING | AuthStatusEnum.AUTHENTICATED | AuthStatusEnum.UNAUTHENTICATED;

export interface UserPayload {
    authStatus: AuthStatus;
    pendingRedirect: boolean;
    setPendingRedirect: React.Dispatch<React.SetStateAction<boolean>>;
    userDetail: CommonResponseModel<userDetailValueModel>;
    setUserDetail: React.Dispatch<React.SetStateAction<CommonResponseModel<userDetailValueModel>>>;
    GetUserMe: () => Promise<void>;
    logout: () => void;
    showLoader: boolean;
    setShowLoader: React.Dispatch<React.SetStateAction<boolean>>;
    activeComponent: activeSectionInfo;
    setActiveComponent: React.Dispatch<React.SetStateAction<activeSectionInfo>>;
}

const UserProvider = ({ children }: ChildrenProps) => {
    const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatusEnum.IDLE);
    const [userDetail, setUserDetail] = useState<CommonResponseModel<userDetailValueModel>>(EMPTY_USER_DETAIL);
    const [showLoader, __setShowLoader] = useState<boolean>(false);
    const [activeComponent, setActiveComponent] = useState<activeSectionInfo>(DEFAULT_ACTIVE_SECTION);
    const [pendingRedirect, setPendingRedirect] = useState(() => !!authService.peekReturnTo());
    const loaderCounter = useRef(0);

    const setShowLoader: React.Dispatch<React.SetStateAction<boolean>> = useCallback((val) => {
        if (val) {
            loaderCounter.current += 1;
            __setShowLoader(true);
        } else {
            loaderCounter.current = Math.max(0, loaderCounter.current - 1);
            if (loaderCounter.current === 0) {
                __setShowLoader(false);
            }
        }
    }, []);

    const GetUserMe = useCallback(async () => {
        if (!authService.isTokenExits()) {
            setAuthStatus(AuthStatusEnum.UNAUTHENTICATED);
            setPendingRedirect(false);
            return;
        }
        setAuthStatus(AuthStatusEnum.LOADING);
        try {
            const response = await agent.UserMeCall.get();
            setUserDetail(response);
            setAuthStatus(AuthStatusEnum.AUTHENTICATED);
        }
        catch (err: any) {
            if(err?.response?.status === 401 || err?.response?.status === 403) {
                authService.removeToken();
                setUserDetail(EMPTY_USER_DETAIL);
            }
            setAuthStatus(AuthStatusEnum.UNAUTHENTICATED);
            setPendingRedirect(false);
        }
    }, []);

    const logout = useCallback(() => {
        authService.removeToken();
        setUserDetail(EMPTY_USER_DETAIL);
        setAuthStatus(AuthStatusEnum.UNAUTHENTICATED);
    }, []);

    useEffect(()=> {
        const tokenFound = authService.syncTokenFromUrl();
        if (tokenFound) {
            const params = new URLSearchParams(globalThis.location.search);
            params.delete(JWT_KEY_FROM_URL);
            const cleanPath = globalThis.location.pathname + (params.toString() ? `?${params}` : '');
            globalThis.history.replaceState({}, '', cleanPath);
        }

        GetUserMe();
    },[])

    const payload: UserPayload = {
        authStatus,
        userDetail,
        pendingRedirect,
        setPendingRedirect,
        setUserDetail,
        GetUserMe,
        logout,
        showLoader,
        setShowLoader,
        activeComponent,
        setActiveComponent
    };

    return (
        <UserContext.Provider value={payload}>
            {children}
        </UserContext.Provider>
    )
}

export { UserContext, UserProvider };
